import { Trans } from 'react-i18next';
import { View } from 'react-native';
import { format } from 'date-fns';
import React, { useMemo, useState } from 'react';

import { Button } from '@components/Button';
import { CircledIcon } from '@components/CircledIcon';
import { Icon } from '@components/Icon';
import { OrderDetailsRow } from '@components/OrderDetailsRow';
import { OrderStatus } from '@components/OrderStatus';
import { ProgressCircle } from '@components/ProgressCircle';
import { TimeBadge } from '@components/TimeBadge';
import { Typography } from '@components/Typography';
import { assignOrder } from '@lib/api/assignOrder';
import { firebase } from '@lib/firebase';
import { logger } from '@lib/logger';
import { lookupsStore } from '@stores/lookups';
import { useLocalizedValue } from '@hooks/useLocalizedValue';
import { useToast } from '@providers/Toast';
import { useTranslation } from '@providers/I18n';
import { Modal, ModalContentLayout } from '@components/Modal';
import { Order, OrderStateEnum } from '@lib/types';
import { makeStyles, useTheme } from '@providers/Theme';

type OrderHeaderProps = {
  order: Order;
  isPrint?: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
    width: '100%',
    paddingBottom: theme.spacing(2),
    borderBottomWidth: 1,
    borderBottomColor: theme.palette.grey[300],
  },
  header: {
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    on: {
      xs: { marginBottom: theme.spacing(1) },
      lg: { marginBottom: theme.spacing(2) },
    },
  },
  details: {
    marginBottom: theme.spacing(2),
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailsWrapper: {
    flex: 3,
  },
  progressWrapper: {
    flex: 1,
    alignItems: 'flex-end',
  },
  assignTxt: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: theme.spacing(0.5),
  },
  assignBtn: {
    display: 'flex',
    flexDirection: 'row',
  },
}));

export const OrderHeader: React.FC<OrderHeaderProps> = ({ order, isPrint }) => {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // FIXED: Moved localized value hooks to the top level
  const restaurantName = useLocalizedValue(order?.orderMessage?.ref?.restaurant?.name);
  const zoneName = useLocalizedValue(order?.orderMessage?.ref?.branch?.zoneName);
  const areaName = useLocalizedValue(order?.orderMessage?.ref?.branch?.areaName);

  const lookups = lookupsStore.getState().value;
  const toastId = `assign-order-toast-${order?.orderMessage?.uuid}`;

  const theme = useTheme();
  const rowColor = theme.palette.base[900];

  const orderAtIftar = order?.orderMessage?.data?.deliveryTime !== 'NOW';

  const MILLI_SEC = 60 * 1000;

  const HANDLING_TIME = firebase.getRemoteConfigNumber('HANDLING_TIME') || 5; // Default to 5 minutes if not set

  const targetTime = useMemo(() => {
    const state = order?.orderMessage?.data?.state;
    const isFleet = order?.orderMessage?.data?.fleet;
    
    if (state === 'ACCEPTED') {
      if (isFleet) {
        return (order?.orderMessage?.data?.expectedCookingDuration || 0) * MILLI_SEC;
      }
      return (order?.orderMessage?.data?.expectedDeliveryDuration || 0) * MILLI_SEC;
    }
    return (HANDLING_TIME ?? 0) * MILLI_SEC;
  }, [
    order?.orderMessage?.data?.state,
    order?.orderMessage?.data?.fleet,
    order?.orderMessage?.data?.expectedCookingDuration,
    order?.orderMessage?.data?.expectedDeliveryDuration,
    HANDLING_TIME,
    MILLI_SEC
  ]);

  const createdTime = useMemo(() => {
    if (order?.orderMessage?.data?.state === 'ACCEPTED') {
      // USE OPTIONAL CHAINING HERE TO PREVENT CRASH
      const acceptedTime = new Date(order?.orderStateTrace?.data?.acceptedTime ?? '');
      return isNaN(acceptedTime.getTime()) ? Date.now() : acceptedTime.getTime();
    }
    return order?.orderMessage?.trace?.created?.at?.getTime() ?? Date.now();
  }, [
    order?.orderMessage?.data?.state,
    order?.orderStateTrace?.data?.acceptedTime,
    order?.orderMessage?.trace?.created?.at
  ]);

  const expectedDeliveryTime = useMemo(() => {
    try {
      // USE OPTIONAL CHAINING
      const baseDate = order?.orderStateTrace?.data?.acceptedTime || order?.orderMessage?.trace?.updated?.at;
      if (!baseDate) return '--:--';

      const acceptedTime = new Date(baseDate);
      const duration = order?.orderMessage?.data?.fleet 
        ? (order?.orderMessage?.data?.expectedCookingDuration ?? 0)
        : (order?.orderMessage?.data?.expectedDeliveryDuration ?? 60);

      acceptedTime.setMinutes(acceptedTime.getMinutes() + duration);
      return format(acceptedTime, 'hh:mm a');
    } catch (e) {
      return '--:--';
    }
  }, [
    order?.orderStateTrace?.data?.acceptedTime,
    order?.orderMessage?.trace?.updated?.at,
    order?.orderMessage?.data?.expectedCookingDuration,
    order?.orderMessage?.data?.expectedDeliveryDuration,
    order?.orderMessage?.data?.fleet
  ]);
  
  const handleClickingAssignOrder = async () => {
    if (
      order?.orderMessage?.data?.assignedLiveTrackerAgent &&
      lookups &&
      order?.orderMessage?.data?.assignedLiveTrackerAgent?.userUUID !==
        lookups?.userUUID
    ) {
      setIsModalOpen(true);
    } else {
      await handleAssignOrder();
    }
  };

  const handleAssignOrder = async () => {
    try {
      toast.show(
        toastId,
        'loading',
        <Typography>
          {t('orderView.assignOrder.toast.loading.title', 'Assign order')}
        </Typography>,
        <Typography>
          {t('orderView.assignOrder.toast.loading.message', 'Assigning order')}{' '}
          <Typography weight="bold">#{order?.orderMessage?.uuid}</Typography> ...
        </Typography>,
      );
      setLoading(true);
      setIsModalOpen(false);
      await assignOrder([order?.orderMessage?.uuid]);
      toast.show(
        toastId,
        'success',
        <Typography>
          {t('orderView.assignOrder.toast.success.title', 'Order assigned')}
        </Typography>,
        <Typography>
          {t(
            'orderView.assignOrder.toast.success.message',
            'Order assign successfully',
          )}
        </Typography>,
      );
    } catch (error) {
      logger.error('assign order error', error);
      toast.show(
        toastId,
        'error',
        <Typography>
          {t('orderView.common.toast.error.title', 'Something went wrong!')}
        </Typography>,
        <Typography>
          {t(
            'orderView.assignOrder.toast.error.message',
            'Something went wrong while assigning order. Please try again later.',
          )}
        </Typography>,
      );
    } finally {
      setLoading(false);
    }
  };

  const branchAddress = useMemo(() => {
    if (order?.orderMessage?.ref?.branch?.address) {
      if (i18n.language === 'ar') {
        return order?.orderMessage?.ref?.branch?.address?.arName + ', ';
      }
      return order?.orderMessage?.ref?.branch?.address?.enName + ', ';
    }
    return '';
  }, [i18n.language, order?.orderMessage?.ref?.branch?.address]);

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <Typography weight="semiBold" size={{ xs: '3xl' }} variant="primary">
          <Trans
            i18nKey="orderDetails.order"
            defaults="Order #{{id}}"
            values={{
              id: order?.orderMessage?.data?.orderShortCode,
            }}
          />
        </Typography>
        {!isPrint && (
          <TimeBadge isIftar={orderAtIftar} pickupTime={expectedDeliveryTime} />
        )}
      </View>

      <View style={styles.details}>
        <View style={styles.detailsWrapper}>
          {!isPrint && <OrderStatus status={order?.orderMessage?.data?.state} />}
          <OrderDetailsRow
            text={t('orderDetails.branch', {
              defaultValue: '{{branch}} Branch',
              branch: `${restaurantName}, ${branchAddress}${zoneName}, ${areaName}`,
            })}
            TypographyProps={{
              size: { xs: 'sm', lg: 'md' },
              weight: 'bold',
            }}
            color={rowColor}
            StartIconProps={{ name: 'pin', size: 14, color: rowColor }}
          />
          <OrderDetailsRow
            // eslint-disable-next-line import/no-named-as-default-member
            text={order?.orderMessage?.trace?.created?.at 
              ? format(order?.orderMessage?.trace?.created?.at, 'yyyy-MM-dd hh:mm a') 
              : '---'}
            TypographyProps={{
              size: { xs: 'sm', lg: 'md' },
              weight: 'bold',
            }}
            color={rowColor}
            StartIconProps={{
              name: 'clock',
              size: 14,
              color: rowColor,
              strokeWidth: '2',
            }}
          />
          {!order?.orderMessage?.data?.fleet && (
            <OrderDetailsRow
              // eslint-disable-next-line import/no-named-as-default-member
              text={
                t('orderView.expectedDeliveryTime', 'Expected delivery time') +
                ': ' +
                expectedDeliveryTime
              }
              TypographyProps={{
                size: { xs: 'sm', lg: 'md' },
                weight: 'semiBold',
              }}
              color={rowColor}
              StartIconProps={{ name: 'hour-glass', size: 14, color: rowColor }}
            />
          )}
        </View>
        {[
          OrderStateEnum.PENDING,
          OrderStateEnum.ROUTED,
          OrderStateEnum.SEEN,
          OrderStateEnum.ACCEPTED,
        ].includes(order?.orderMessage?.data?.state) &&
          !isPrint && (
            <View style={styles.progressWrapper}>
              <ProgressCircle targetTime={targetTime} startTime={createdTime} />
            </View>
          )}
      </View>

      {!isPrint && order?.orderMessage?.data?.assignedLiveTrackerAgent && (
        <View style={styles.assignTxt}>
          <Typography>
            <Trans
              i18nKey="orderDetails.assign"
              defaults="<bold>Assigned to:</bold> <regular> {{name}}</regular>"
              values={{
                name: order?.orderMessage?.data?.assignedLiveTrackerAgent?.userCode,
              }}
              components={{
                bold: (
                  // @ts-ignore
                  <Typography
                    weight="semiBold"
                    size={{ xs: 'sm', lg: 'md' }}
                    variant="success"
                  />
                ),
                regular: (
                  // @ts-ignore
                  <Typography
                    weight="regular"
                    size={{ xs: 'sm', lg: 'md' }}
                    variant="success"
                  />
                ),
              }}
            />
          </Typography>
        </View>
      )}
      {!isPrint &&
        ['PENDING', 'ROUTED', 'SEEN'].includes(order?.orderMessage?.data?.state) &&
        order?.orderMessage?.data?.assignedLiveTrackerAgent?.userUUID !==
          lookups?.userUUID && (
          <View style={styles.assignBtn}>
            <Button
              variant="outlined"
              startIcon={
                <Icon name="dot" size={10} color={theme.palette.success[400]} />
              }
              onPress={handleClickingAssignOrder}
              loading={loading}>
              <Trans i18nKey="orderDetails.assignToMe" />
            </Button>
          </View>
        )}

      <Modal open={isModalOpen} onRequestClose={() => setIsModalOpen(false)}>
        <ModalContentLayout
          icon={<CircledIcon icon="alert-triangle" preset="warning" />}
          title={t('orderView.reassignOrder.modal.title', {
            defaultValue: 'This order is already assigned to {{value}}',
            value: order?.orderMessage?.data?.assignedLiveTrackerAgent?.userCode,
          })}
          subtitle={t('orderView.reassignOrder.modal.subtitle', {
            defaultValue: 'Are you sure you want to re-assign it to you?',
          })}
          actions={[
            <Button variant="primary" onPress={handleAssignOrder}>
              {t('orderView.reassignModal.reassignButton', {
                defaultValue: 'Re-assign this order to me',
              })}
            </Button>,
            <Button variant="secondary" onPress={() => setIsModalOpen(false)}>
              {t('orderView.reassignModal.cancelButton', {
                defaultValue: 'Cancel',
              })}
            </Button>,
          ]}
        />
      </Modal>
    </View>
  );
};
